import { Controller } from "@hotwired/stimulus"

// Fullscreen swipeable image viewer for review photos. One controller per
// review card. Each card carries its own set of images and its own open/
// closed state, so two cards open simultaneously don't fight each other.
//
// Markup expected (inside a single review card):
//   <div data-controller="lightbox">
//     <button data-action="click->lightbox#open" data-lightbox-index-param="0">
//       <img src=".../thumb..." alt="...">
//     </button>
//     <button data-action="click->lightbox#open" data-lightbox-index-param="1"> ... </button>
//
//     <div data-lightbox-target="overlay" class="hidden ...">
//       <div data-lightbox-target="slide" data-src="...medium..." class="hidden"></div>
//       <div data-lightbox-target="slide" data-src="...medium..." class="hidden"></div>
//       <span data-lightbox-target="counter"></span>
//       <button data-action="click->lightbox#prev"> ... </button>
//       <button data-action="click->lightbox#next"> ... </button>
//       <button data-action="click->lightbox#close"> ... </button>
//     </div>
//   </div>
//
// Each slide is an empty div whose data-src points at the :medium variant
// (800×800). We only inject the <img> on first display so the page doesn't
// download 5 medium variants per review on every page render. Lazy fill.
export default class extends Controller {
  static targets = ["overlay", "slide", "counter"]

  connect() {
    this.currentIndex = 0
    this.boundKey = this.handleKey.bind(this)
    this.touchStartX = null
    this.touchStartY = null
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKey)
    document.body.classList.remove("overflow-hidden")
  }

  open(event) {
    event.preventDefault()
    this.currentIndex = parseInt(event.params.index, 10) || 0
    this.overlayTarget.classList.remove("hidden")
    // Lock background scroll while the overlay is open
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.boundKey)
    this.render()
  }

  close() {
    this.overlayTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.boundKey)
  }

  // Stops a tap on the slide itself from triggering the backdrop close
  stopPropagation(event) {
    event.stopPropagation()
  }

  next() {
    if (this.slideTargets.length === 0) return
    this.currentIndex = (this.currentIndex + 1) % this.slideTargets.length
    this.render()
  }

  prev() {
    if (this.slideTargets.length === 0) return
    this.currentIndex =
      (this.currentIndex - 1 + this.slideTargets.length) % this.slideTargets.length
    this.render()
  }

  // ESC closes; arrow keys navigate. Only active while the overlay is open.
  handleKey(event) {
    if (event.key === "Escape") this.close()
    else if (event.key === "ArrowRight") this.next()
    else if (event.key === "ArrowLeft") this.prev()
  }

  // Swipe gestures on mobile. >50px horizontal swipe (with <80px vertical
  // drift) triggers navigation. The thresholds prevent accidental swipes
  // when the user is just scrolling or tapping.
  touchStart(event) {
    const t = event.changedTouches?.[0]
    if (!t) return
    this.touchStartX = t.clientX
    this.touchStartY = t.clientY
  }

  touchEnd(event) {
    if (this.touchStartX == null) return
    const t = event.changedTouches?.[0]
    if (!t) return
    const dx = t.clientX - this.touchStartX
    const dy = Math.abs(t.clientY - this.touchStartY)
    this.touchStartX = null
    this.touchStartY = null
    if (Math.abs(dx) < 50 || dy > 80) return  // ignore tiny / mostly-vertical swipes
    if (dx < 0) this.next()
    else this.prev()
  }

  render() {
    this.slideTargets.forEach((slide, i) => {
      const isActive = i === this.currentIndex
      slide.classList.toggle("hidden", !isActive)
      // Lazy-fill the <img> the first time this slide becomes active.
      // Shows a centered spinner immediately, swaps to the <img> once it
      // finishes downloading (img.onload). Removes the spinner so the
      // image sits clean. Failure (onerror) hides the spinner too so we
      // don't leave a stuck "loading" state.
      if (isActive && !slide.dataset.loaded) {
        const src = slide.dataset.src
        if (src) {
          const spinner = document.createElement("div")
          spinner.className =
            "absolute inset-0 flex items-center justify-center text-white/80 text-3xl"
          spinner.innerHTML = '<i class="fas fa-circle-notch fa-spin"></i>'
          slide.appendChild(spinner)

          const img = document.createElement("img")
          img.alt = slide.dataset.alt || ""
          img.className = "max-w-full max-h-full object-contain opacity-0 transition-opacity duration-200"
          img.loading = "eager"
          img.onload = () => {
            spinner.remove()
            img.classList.remove("opacity-0")
          }
          img.onerror = () => spinner.remove()
          img.src = src
          slide.appendChild(img)
          slide.dataset.loaded = "true"
        }
      }
    })
    if (this.hasCounterTarget) {
      this.counterTarget.textContent =
        `${this.currentIndex + 1} / ${this.slideTargets.length}`
    }
  }
}
